import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createTenant,
  editTenant,
  emptyTenantWorkConfig,
  getTenant,
  parseTenantCurrencies,
  parseTenantTimezones,
  parseTenantWorkConfig,
  tenantWorkConfigSnapshot,
  type ITenantCurrency,
  type ITenantTimezone,
  type ITenantWorkConfig,
} from '../../services/tenantService';
import TenantCurrencyFields from './TenantCurrencyFields';
import TenantTimezoneFields from './TenantTimezoneFields';
import TenantPricingFields, {
  type TenantPricingValue,
} from './TenantPricingFields';
import TenantWorkFields from './TenantWorkFields';
import { v4 as uuid } from 'uuid';
import { useBoolean } from '@fluentui/react-hooks';
import UserOrGroupSelector from '../../common/selectors/UsersOrGroupSelectors';
import { Stack, Text } from '@fluentui/react';
import { makeStyles, tokens } from '@fluentui/react-components';
import {
  PersonAvailable24Regular,
  NumberSymbolSquare24Regular,
} from '@fluentui/react-icons';
import CodeConventionPanel from './CodeConventionPanel';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  getPricingTiers,
  type IPricingTier,
} from '../../services/settingsService';
import { tenantAtom } from '../../atoms/tenantAtom';
import { useTranslation } from 'react-i18next';
import {
  EditItem,
  FIELD_MAX_WIDTH,
  TENANT_THEME_KEY,
  THEME_OPTIONS,
} from '@moko/ui';
import OtherInfo from '../../common/OtherInfoPortal';
import { FEATURE_CONFIG } from '../../libs/featureAccess';

interface IUserOrGroup {
  id: number;
  isGroup: boolean;
  username?: string;
  name?: string;
}

const optionalNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const useStyles = makeStyles({
  otherInfo: {
    paddingLeft: tokens.spacingHorizontalL,
    '& [id^="field_"]': {
      maxWidth: `${FIELD_MAX_WIDTH}px`,
    },
  },
});

const Tenant = () => {
  const { t } = useTranslation();
  const styles = useStyles();

  const [
    isAccessSelectorOpen,
    { setTrue: showAccessSelector, setFalse: hideAccessSelector },
  ] = useBoolean(false);
  const [
    isCodesPanelOpen,
    { setTrue: showCodesPanel, setFalse: hideCodesPanel },
  ] = useBoolean(false);
  const [tenantOtherInfo, setTenantOtherInfo] = useState<any>({});
  const [tenantCurrencies, setTenantCurrencies] = useState<ITenantCurrency[]>(
    []
  );
  const [initialCurrencies, setInitialCurrencies] = useState('[]');
  const [tenantTimezones, setTenantTimezones] = useState<ITenantTimezone[]>([]);
  const [initialTimezones, setInitialTimezones] = useState('[]');
  const [workConfig, setWorkConfig] = useState<ITenantWorkConfig>(
    emptyTenantWorkConfig()
  );
  const [initialWorkConfig, setInitialWorkConfig] = useState(() =>
    tenantWorkConfigSnapshot(emptyTenantWorkConfig())
  );
  const [accesses, setAccesses] = useState<IUserOrGroup[]>();
  const isSaved = useRef(false);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const [pricingTiers, setPricingTiers] = useState<IPricingTier[]>([]);
  const [recordName, setRecordName] = useState('');
  const [pricing, setPricing] = useState<TenantPricingValue>({
    pricingTierId: null,
    pricingDiscountPercentage: null,
  });
  const patchValuesRef = useRef<
    ((patch: Record<string, unknown>) => void) | null
  >(null);
  const currentTenant = useAtomValue(tenantAtom);
  const setTenant = useSetAtom(tenantAtom);

  useEffect(() => {
    const abortController = new AbortController();
    getPricingTiers(abortController)
      .then((tiers) => {
        setPricingTiers(
          [...(tiers ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)
        );
      })
      .catch((error: { message?: string }) => {
        if (abortController.signal.aborted) return;
        setErrorMessage(error?.message || 'Failed to load pricing tiers.');
      });
    return () => abortController.abort();
  }, [setErrorMessage]);

  const otherInfoMetadata = useMemo(
    () => ({
      tabs: [
        { order: 1, tabName: 'general', caption: 'General' },
        { order: 2, tabName: 'pricing', caption: 'Pricing' },
        { order: 3, tabName: 'work', caption: 'Work and certifications' },
        { order: 4, tabName: 'license', caption: 'License' },
        { order: 5, tabName: 'styles', caption: 'Styles' },
        { order: 6, tabName: 'features', caption: 'Features' },
        { order: 7, tabName: 'ui', caption: 'UI' },
        { order: 8, tabName: 'contacts', caption: 'Contacts' },
      ],
      fields: [
        ...FEATURE_CONFIG.map((feature) => ({
          name: feature.key,
          fieldType: 'Checkbox',
          label: feature.name,
          tabName: 'features',
        })),
        {
          name: 'logo',
          fieldType: 'String',
          label: 'Logo',
          tabName: 'styles',
        },
        {
          name: 'title',
          fieldType: 'String',
          label: 'Title',
          tabName: 'ui',
        },
        {
          name: 'subtitle',
          fieldType: 'String',
          label: 'Subtitle',
          tabName: 'ui',
          hint: 'Leave empty for the default "Field management system". Enter a single space to hide the subtitle.',
        },
        {
          name: 'distanceUnit',
          fieldType: 'Lookup',
          label: 'Distance unit',
          lookupList: 'DistanceUnits',
          tabName: 'general',
        },
        {
          name: 'titleStyle',
          fieldType: 'String',
          label: 'Title Style',
          tabName: 'styles',
        },
        {
          name: TENANT_THEME_KEY,
          fieldType: 'Lookup',
          label: 'Theme',
          lookupList: 'Themes',
          tabName: 'styles',
        },
        {
          name: 'languages',
          fieldType: 'Lookup',
          multiSelect: true,
          label: 'Languages',
          lookupList: 'Languages',
          tabName: 'general',
        },
        {
          name: 'standardInstruction',
          fieldType: 'String',
          label: 'Standard instruction',
          rows: 4,
          tabName: 'general',
        },
        {
          name: 'purchaseRef',
          fieldType: 'String',
          label: 'Purchase reference',
          tabName: 'license',
        },
        {
          name: 'purchaseDate',
          fieldType: 'Date',
          label: 'Purchase date',
          tabName: 'license',
        },
        {
          name: 'assetCount',
          fieldType: 'Number',
          label: 'Asset count',
          tabName: 'license',
        },
        {
          name: 'leftNav',
          fieldType: 'Panel',
          label: 'Client: Left Navigation',
          tabName: 'ui',
        },
        {
          name: 'showAssetInLeftNav',
          fieldType: 'Checkbox',
          label: 'Show Assets',
          tabName: 'ui',
          parentName: 'leftNav',
        },
        {
          name: 'mngrWork',
          fieldType: 'Panel',
          label: 'Manager: Create Work',
          tabName: 'ui',
        },
        {
          name: 'showAssetInCreateWork',
          fieldType: 'Checkbox',
          label: 'Show Asset field',
          tabName: 'ui',
          parentName: 'mngrWork',
        },
        {
          name: 'work',
          fieldType: 'Panel',
          label: 'Client: Work',
          tabName: 'ui',
        },
        {
          name: 'showCreateWork',
          fieldType: 'Checkbox',
          label: 'Show Create Work',
          tabName: 'ui',
          parentName: 'work',
        },
        {
          name: 'contactName',
          fieldType: 'String',
          label: 'Contact Name',
          tabName: 'contacts',
        },
        {
          name: 'contactEmail',
          fieldType: 'String',
          label: 'Contact Email',
          tabName: 'contacts',
        },
        {
          name: 'contactPhone',
          fieldType: 'String',
          label: 'Contact Phone',
          tabName: 'contacts',
        },
      ],
      lookups: [
        {
          name: 'Languages',
          values: [
            { key: 'EN', value: 'English' },
            { key: 'DE', value: 'Germany' },
            { key: 'CN', value: 'Chinese' },
          ],
        },
        {
          name: 'DistanceUnits',
          values: [
            { key: 'Km', value: 'Kilometer' },
            { key: 'Miles', value: 'Miles' },
          ],
        },
        {
          name: 'Themes',
          values: THEME_OPTIONS.map((option) => ({
            key: option.key,
            value: option.label,
          })),
        },
      ],
      validations: [],
      interactions: [],
    }),
    []
  );

  return (
    <>
      <EditItem
        pinToolbar
        pinnedHeader={
          <Text variant="xLarge">{recordName.trim() || t('tenant')}</Text>
        }
        getAction={async (abortController, id) => {
          const tenant = await getTenant(abortController, id);
          setRecordName(tenant.name ?? '');
          setPricing({
            pricingTierId: optionalNumber(tenant.pricingTierId),
            pricingDiscountPercentage: optionalNumber(
              tenant.pricingDiscountPercentage
            ),
          });
          setTenantOtherInfo(tenant.otherInfo);
          const currencies = parseTenantCurrencies(tenant.currencies);
          setTenantCurrencies(currencies);
          setInitialCurrencies(JSON.stringify(currencies));
          const timezones = parseTenantTimezones(tenant.timezones);
          setTenantTimezones(timezones);
          setInitialTimezones(JSON.stringify(timezones));
          const config = parseTenantWorkConfig(tenant);
          setWorkConfig(config);
          setInitialWorkConfig(tenantWorkConfigSnapshot(config));
          setAccesses(
            tenant.users
              ?.map((user) => ({
                id: user.id,
                isGroup: false,
                name: user.username,
              }))
              .concat(
                tenant.groups?.map((group) => ({
                  id: group.id,
                  isGroup: true,
                  name: group.name,
                })) ?? []
              )
          );
          return tenant;
        }}
        newAction={async (abortController, newTenant) => {
          const tenant = await createTenant(abortController, {
            name: newTenant.name as string,
            code: newTenant.code as string,
            description: newTenant.description as string,
            uuid: newTenant.uuid as string,
            usersIds: accesses
              ?.filter((access) => !access.isGroup)
              .map((access) => access.id),
            groupsIds: accesses
              ?.filter((access) => access.isGroup)
              .map((access) => access.id),
            otherInfo: tenantOtherInfo,
            currencies: tenantCurrencies,
            timezones: tenantTimezones,
            pricingTierId: optionalNumber(newTenant.pricingTierId),
            pricingDiscountPercentage: optionalNumber(
              newTenant.pricingDiscountPercentage
            ),
            certificationTypeIds: workConfig.certificationTypeIds,
            workTypes: workConfig.workTypes,
          });
          setInitialCurrencies(JSON.stringify(tenantCurrencies));
          setInitialTimezones(JSON.stringify(tenantTimezones));
          setInitialWorkConfig(tenantWorkConfigSnapshot(workConfig));
          setSuccessMessage(`Tenant with id: ${tenant.tenantId} created.`);
        }}
        editAction={async (abortController, id, editedTenant) => {
          await editTenant(abortController, id, {
            name: editedTenant.name as string,
            code: editedTenant.code as string,
            description: editedTenant.description as string,
            usersIds: accesses
              ?.filter((access) => !access.isGroup)
              .map((access) => access.id),
            groupsIds: accesses
              ?.filter((access) => access.isGroup)
              .map((access) => access.id),
            otherInfo: tenantOtherInfo,
            currencies: tenantCurrencies,
            timezones: tenantTimezones,
            pricingTierId: optionalNumber(editedTenant.pricingTierId),
            pricingDiscountPercentage: optionalNumber(
              editedTenant.pricingDiscountPercentage
            ),
            certificationTypeIds: workConfig.certificationTypeIds,
            workTypes: workConfig.workTypes,
          });
          // Refresh tenant atom if editing current tenant
          if (currentTenant?.id === id) {
            const updatedTenant = await getTenant(abortController, id);
            setTenant(updatedTenant);
          }
          setInitialCurrencies(JSON.stringify(tenantCurrencies));
          setInitialTimezones(JSON.stringify(tenantTimezones));
          setInitialWorkConfig(tenantWorkConfigSnapshot(workConfig));
          setSuccessMessage(`Tenant with id: ${id} successfully updated.`);
        }}
        isSaved={isSaved}
        isDirtyExternal={
          JSON.stringify(tenantCurrencies) !== initialCurrencies ||
          JSON.stringify(tenantTimezones) !== initialTimezones ||
          tenantWorkConfigSnapshot(workConfig) !== initialWorkConfig
        }
        back={'/security?tab=tenants'}
        preButtons={[
          {
            text: 'Access',
            icon: <PersonAvailable24Regular />,
            onClick: () => showAccessSelector(),
          },
        ]}
        postButtons={[
          {
            text: 'Codes',
            icon: <NumberSymbolSquare24Regular />,
            onClick: () => showCodesPanel(),
          },
        ]}
        patchValuesRef={patchValuesRef}
        afterChange={(item) => {
          setRecordName(typeof item.name === 'string' ? item.name : '');
        }}
        defaultValues={JSON.stringify({ uuid: uuid() })}
        metadata={{
          fields: [
            {
              name: 'name',
              fieldType: 'String',
              label: 'Name',
            },
            {
              name: 'code',
              fieldType: 'String',
              label: 'Code',
              mandatory: true,
            },
            {
              name: 'description',
              fieldType: 'String',
              label: 'Description',
            },
            {
              name: 'uuid',
              fieldType: 'String',
              label: 'Unique Id',
            },
          ],
          lookups: [],
          validations: [
            {
              field: 'code',
              rules: [
                {
                  rule: 'NE',
                  errorMessage: "Code shouldn't be empty.",
                },
                {
                  rule: 'NGT10',
                  errorMessage: "Code shouldn't be longer than 10 chars.",
                },
              ],
            },
          ],
          interactions: [],
        }}
      >
        <Stack styles={{ root: { width: 760 } }} tokens={{ childrenGap: 16 }}>
          <Stack>
            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
              OtherInfo
            </Text>
            <div className={styles.otherInfo}>
              <OtherInfo
                metadata={otherInfoMetadata}
                otherInfo={tenantOtherInfo ?? {}}
                isSaved={isSaved}
                onOtherInfoChanged={(newValue: string) => {
                  setTenantOtherInfo(JSON.stringify(newValue));
                }}
                hideTitle
                renderTab={(tabName) => {
                  if (tabName === 'pricing') {
                    return (
                      <TenantPricingFields
                        value={pricing}
                        tiers={pricingTiers}
                        onChange={(next) => {
                          setPricing(next);
                          patchValuesRef.current?.({
                            pricingTierId: next.pricingTierId,
                            pricingDiscountPercentage:
                              next.pricingDiscountPercentage,
                          });
                        }}
                      />
                    );
                  }
                  if (tabName === 'work') {
                    return (
                      <TenantWorkFields
                        value={workConfig}
                        onChange={setWorkConfig}
                      />
                    );
                  }
                  return null;
                }}
                renderBeforeField={(fieldName) =>
                  fieldName === 'distanceUnit' ? (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        rowGap: 16,
                        width: '100%',
                      }}
                    >
                      <TenantTimezoneFields
                        value={tenantTimezones}
                        onChange={setTenantTimezones}
                      />
                      <TenantCurrencyFields
                        value={tenantCurrencies}
                        onChange={setTenantCurrencies}
                      />
                    </div>
                  ) : null
                }
              />
            </div>
          </Stack>
        </Stack>
      </EditItem>
      <UserOrGroupSelector
        isOpen={isAccessSelectorOpen}
        onUserOrGroupSelected={(
          id: number,
          isGroup: boolean,
          username?: string
        ) => {
          setAccesses([...(accesses ?? []), { id, isGroup, name: username }]);
        }}
        hideUsersAndGroupsSelector={hideAccessSelector}
        permissions={accesses}
        onRemovePermission={(id) =>
          setAccesses(accesses?.filter((a) => a.id !== id) ?? [])
        }
      />
      <CodeConventionPanel
        isOpen={isCodesPanelOpen}
        onDismiss={hideCodesPanel}
        tenantOtherInfo={tenantOtherInfo}
        onFormatChanged={(workFormat, assetFormat, orderFormat) => {
          try {
            const info =
              typeof tenantOtherInfo === 'string'
                ? JSON.parse(tenantOtherInfo)
                : tenantOtherInfo ?? {};
            if (workFormat !== undefined) {
              info['work-code-convention'] = { format: workFormat };
            }
            if (assetFormat !== undefined) {
              info['asset-code-convention'] = { format: assetFormat };
            }
            if (orderFormat !== undefined) {
              info['order-code-convention'] = { format: orderFormat };
            }
            setTenantOtherInfo(JSON.stringify(info));
          } catch {
            // noop
          }
        }}
      />
    </>
  );
};

export default Tenant;
